import React from "react";
import styled from "styled-components";
import { ReactComponent as CheckDisabled } from "../../../assets/icons/checkboxVectorDisabled.svg";
import { ReactComponent as CheckNormal } from "../../../assets/icons/checkboxVectorNormal.svg";
import { ReactComponent as CheckEnabled } from "../../../assets/icons/checkboxVectorSelected.svg";

// #region region styles
const CheckBoxStyles = styled.div`
    .main-checkbox {
        width: 16px;
        height: 16px;
    }

    .disabled {
        color: #4f5b6d;
        cursor: not-allowed;
    }
`;
// #endregion

/** Checkbox component renders a customizable checkbox.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.className=""] - Additional class names for the checkbox.
 * @param {Object} [props.style] - Inline styles for the checkbox.
 * @param {boolean} [props.isChecked=false] - Determines if the checkbox is checked.
 * @param {Function} [props.onCheckboxClick=() => {}] - Callback function to handle checkbox click events.
 * @param {string} [props.disabled=false] - Determines if the checkbox is disabled.
 * @param {string} [props.checkboxColor="var(--App-Accent-Outline)"] - The color of the checkbox.
 * @param {boolean} [props.useGreenColorVariant=true] - Determines if the checkbox should use the green color variant. Note: Only works when checkboxColor is not set.
 *
 * @returns {JSX.Element} The rendered Checkbox component.
 */
export default React.forwardRef((props, ref) => {
    const {
        className = "",
        isChecked = false,
        onCheckboxClick = () => {},
        style = {},
        disabled = false,
        useGreenColorVariant = true
    } = props;
    const greenColor = useGreenColorVariant && !props?.checkboxColor && "var(--Buttons-Green-Outline)";

    const getCheckboxImage = () => {
        if (disabled) {
            if (!isChecked) return <CheckDisabled className={"main-checkbox cursor-not-allowed"} ref={ref} />;
            else return <CheckEnabled className={"main-checkbox cursor-not-allowed disabled"} ref={ref} />;
        }
        if (isChecked) return <CheckEnabled className={"main-checkbox cursor-pointer"} ref={ref} />;
        return <CheckNormal className={"main-checkbox cursor-pointer"} ref={ref} />;
    };

    // #region render
    return (
        <CheckBoxStyles
            style={{
                color: props?.checkboxColor || greenColor || "var(--App-Accent-Outline)",
                height: "16px",
                ...style
            }}
            className={`checkbox-standard ${className} ${disabled ? "disabled" : ""}`}
            onClick={disabled ? null : onCheckboxClick}>
            {getCheckboxImage()}
        </CheckBoxStyles>
    );
    // #endregion
});
